import * as React from 'react';
import styled from 'styled-components';
import { MenuItemContent, IMenuItemRenderProps, IMenuItem } from '../common';
import { Callout } from '../../callout';
import { CursorType } from '../common/types';

const SubMenuWrapper = styled.div.attrs(() => ({
    className: String('ap-flyoutMenu-subMenuWrapper')
}))`
    box-sizing: border-box;
    user-select: none;
    white-space: nowrap;
    overflow: hidden;
`;

interface IFlyoutMenuItemProps extends Omit<IMenuItemRenderProps, 'onSelect'> {
    className?: string;
    cursorType?: CursorType;
    shouldRenderSubMenuItems?: boolean;
    isItemHovered: boolean;
    getSubMenuItems(id: string): IMenuItem[];
    onSelect?(id: string): void;
    onItemHovered(id: string): void;
    onRenderItem?(renderProps: IMenuItemRenderProps): React.ReactNode;
    prepareItemToRender(item: IMenuItem): React.ReactNode;
}

export const FlyoutMenuItem = React.memo((props: IFlyoutMenuItemProps) => {
    const { className, itemInfo, isItemHovered, hasChildren, getSubMenuItems, prepareItemToRender, onItemHovered, onSelect, onRenderItem, shouldRenderSubMenuItems, ...rest } = props;
    const menuItemRef = React.useRef<HTMLDivElement>(null);
    const { id } = itemInfo;

    const renderSubItems = React.useCallback(() => (
        <Callout
            renderAdjacentToTarget
            targetRef={menuItemRef}
            hidden={!shouldRenderSubMenuItems}
            autofocusOnOpen
            closeOnEscape>
            <SubMenuWrapper>
                {getSubMenuItems(id).map(prepareItemToRender)}
            </SubMenuWrapper>
        </Callout>
    ), [getSubMenuItems, id, prepareItemToRender, shouldRenderSubMenuItems]);

    const _onMouseEnter = React.useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        onItemHovered(id);
    }, [id, onItemHovered]);

    const _onSelect = React.useCallback(() => {
        if (onSelect) {
            onSelect(id);
        }
    }, [id, onSelect]);

    return (
        <React.Fragment>
            <div
                ref={menuItemRef}
                className={className}
                onMouseEnter={_onMouseEnter}>
                {
                    onRenderItem
                        ? onRenderItem({ ...rest, hasChildren, itemInfo, isItemHovered, onSelect: _onSelect })
                        : <MenuItemContent
                            {...rest}
                            hasChildren={hasChildren}
                            itemInfo={itemInfo}
                            isItemHovered={isItemHovered}
                            onSelect={_onSelect} />
                }
            </div>
            {hasChildren ? renderSubItems() : null}
        </React.Fragment>
    );
});

FlyoutMenuItem.displayName = 'FlyoutMenuItem';
